import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Crown, Check, Lightning, Users, Receipt, 
  ChartLine, Wallet, WhatsappLogo, Star, ArrowLeft,
  ShieldCheck, Sparkle, Gift, Clock
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const PremiumPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [razorpayKeyId, setRazorpayKeyId] = useState(null);

  const benefits = [
    { icon: Users, title: 'Unlimited Enquiry', desc: 'सर्व enquiry track करा' },
    { icon: Receipt, title: 'Professional Invoices', desc: 'WhatsApp वर share करा' },
    { icon: Wallet, title: 'Payment Tracking', desc: 'Pending payments track करा' },
    { icon: ChartLine, title: 'Business Analytics', desc: 'Revenue analytics' },
    { icon: WhatsappLogo, title: 'WhatsApp Integration', desc: 'Direct messaging' },
    { icon: Star, title: 'Remove Branding', desc: 'Profile वरून branding हटवा' },
  ];

  const plans = {
    trial: {
      name: 'Free Trial',
      price: 0,
      displayPrice: 'FREE',
      duration: '1 महिना',
      popular: false
    },
    monthly: {
      name: 'Monthly',
      price: 99,
      displayPrice: '₹99',
      duration: '/month',
      popular: false
    },
    yearly: {
      name: 'Yearly',
      price: 999,
      displayPrice: '₹999',
      duration: '/year',
      originalPrice: '₹1188',
      savings: '2 महिने FREE!',
      popular: true
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPlans();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscription/plans`);
      if (response.data.razorpay_key_id) {
        setRazorpayKeyId(response.data.razorpay_key_id);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleStartTrial = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('कृपया आधी Login करा');
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/subscription/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      localStorage.setItem('is_premium', 'true');
      toast.success(response.data.message);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trial start failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyPremium = async (plan) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('कृपया आधी Login करा');
      navigate('/auth');
      return;
    }

    if (plan === 'trial') {
      handleStartTrial();
      return;
    }

    if (!razorpayKeyId) {
      toast.error('Payment gateway configured नाही. Admin ला contact करा.');
      return;
    }

    setLoading(true);
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway load failed. पुन्हा try करा.');
        setLoading(false);
        return;
      }

      // Create order
      const orderResponse = await axios.post(`${API_URL}/subscription/create-order`, 
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { order_id, amount, key_id } = orderResponse.data;

      // Get user info
      const userResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = userResponse.data;

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'Designbiz.in',
        description: `${plans[plan].name} Subscription`,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${API_URL}/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: plan
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            localStorage.setItem('is_premium', 'true');
            toast.success(verifyResponse.data.message);
            navigate('/dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.mobile
        },
        theme: {
          color: '#FF5A36'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setLoading(false);
    }
  };

  // If already premium
  if (subscriptionStatus?.is_active) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50 py-12">
        <div className="max-w-md mx-auto px-4">
          <Card className="border-2 border-green-500 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Crown className="h-10 w-10 text-green-600" weight="fill" />
              </div>
              <h2 className="font-['Chivo'] text-2xl font-black text-green-600 mb-2">
                Premium Active! 🎉
              </h2>
              <p className="text-gray-600 mb-4">
                Plan: <span className="font-bold">{subscriptionStatus.plan}</span>
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Valid till: {new Date(subscriptionStatus.end_date).toLocaleDateString('mr-IN')}
              </p>
              <Button onClick={() => navigate('/dashboard')} className="bg-green-600 hover:bg-green-700 text-white rounded-full">
                Dashboard वर जा →
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5 mr-2" /> Back
          </Button>
          <h1 className="font-['Chivo'] text-xl font-black text-[#FF5A36]">
            Designbiz.in
          </h1>
          <div className="w-20"></div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0 mb-4 px-4 py-2">
            <Sparkle className="h-4 w-4 mr-1" weight="fill" /> SPECIAL OFFER
          </Badge>
          <h1 className="font-['Chivo'] text-3xl md:text-4xl font-black text-gray-900 mb-2">
            Premium Plan 
            <Crown className="inline h-8 w-8 text-yellow-500 ml-2" weight="fill" />
          </h1>
          <p className="text-gray-600">
            तुमच्या Design Business ला next level वर घेऊन जा!
          </p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Free Trial */}
          <Card 
            className={`cursor-pointer transition-all ${selectedPlan === 'trial' ? 'border-2 border-green-500 shadow-lg' : 'border border-gray-200 hover:border-green-300'}`}
            onClick={() => setSelectedPlan('trial')}
          >
            <CardContent className="pt-6 pb-6 text-center">
              <Gift className="h-8 w-8 text-green-600 mx-auto mb-2" weight="duotone" />
              <h3 className="font-bold text-lg mb-1">Free Trial</h3>
              <p className="text-3xl font-black text-green-600">FREE</p>
              <p className="text-sm text-gray-500">1 महिना</p>
              <div className="mt-3 text-xs text-green-600 font-medium">
                <Clock className="inline h-3 w-3 mr-1" />
                No Credit Card Required
              </div>
            </CardContent>
          </Card>

          {/* Monthly */}
          <Card 
            className={`cursor-pointer transition-all ${selectedPlan === 'monthly' ? 'border-2 border-orange-500 shadow-lg' : 'border border-gray-200 hover:border-orange-300'}`}
            onClick={() => setSelectedPlan('monthly')}
          >
            <CardContent className="pt-6 pb-6 text-center">
              <h3 className="font-bold text-lg mb-1">Monthly</h3>
              <p className="text-3xl font-black text-gray-900">₹99</p>
              <p className="text-sm text-gray-500">/month</p>
              <div className="mt-3 text-xs text-gray-500">
                Cancel anytime
              </div>
            </CardContent>
          </Card>

          {/* Yearly - Popular */}
          <Card 
            className={`cursor-pointer transition-all relative overflow-hidden ${selectedPlan === 'yearly' ? 'border-2 border-orange-500 shadow-xl scale-105' : 'border border-gray-200 hover:border-orange-300'}`}
            onClick={() => setSelectedPlan('yearly')}
          >
            <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
              BEST VALUE
            </div>
            <CardContent className="pt-6 pb-6 text-center">
              <h3 className="font-bold text-lg mb-1">Yearly</h3>
              <p className="text-sm text-gray-400 line-through">₹1188</p>
              <p className="text-3xl font-black text-orange-600">₹999</p>
              <p className="text-sm text-gray-500">/year</p>
              <Badge className="mt-2 bg-green-100 text-green-700 border-0">
                2 महिने FREE! 🎁
              </Badge>
            </CardContent>
          </Card>
        </div>

        {/* Benefits */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h3 className="font-bold text-lg mb-4 text-center">Premium मध्ये मिळेल:</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {benefits.map((benefit, i) => {
                const Icon = benefit.icon;
                return (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-green-600" weight="bold" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{benefit.title}</p>
                      <p className="text-xs text-gray-500">{benefit.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            onClick={() => handleBuyPremium(selectedPlan)}
            disabled={loading}
            className="bg-gradient-to-r from-[#FF5A36] to-orange-500 hover:from-[#E04828] hover:to-orange-600 text-white rounded-full px-8 py-6 text-lg font-bold shadow-xl"
          >
            {loading ? 'Processing...' : (
              selectedPlan === 'trial' ? (
                <>
                  <Gift className="h-5 w-5 mr-2" weight="fill" />
                  Free Trial सुरू करा
                </>
              ) : (
                <>
                  <Lightning className="h-5 w-5 mr-2" weight="fill" />
                  {plans[selectedPlan].displayPrice} मध्ये Subscribe करा
                </>
              )
            )}
          </Button>
          
          <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <ShieldCheck className="h-4 w-4 text-green-600" weight="fill" />
              Secure Payment
            </span>
            <span>•</span>
            <span>Cancel Anytime</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumPage;
